import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, addDoc, query, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

interface LocationSuggestion {
  id: string;
  name: string;
  address: string;
  type: 'restaurant' | 'cafe' | 'office' | 'public' | 'other';
  suggestedBy: string;
  status: 'proposed' | 'accepted' | 'declined';
  timestamp: Date;
}

interface MeetingProposal {
  id: string;
  date: string;
  time: string;
  location: LocationSuggestion;
  duration: string;
  notes: string;
  proposedBy: string;
  status: 'proposed' | 'accepted' | 'declined' | 'confirmed';
}

interface PhysicalMeetingInterfaceProps {
  connectionId: string;
  currentUserId?: string;
}

export const PhysicalMeetingInterface: React.FC<PhysicalMeetingInterfaceProps> = ({ 
  connectionId,
  currentUserId = 'user123' 
}) => {
  const [activeTab, setActiveTab] = useState<'plan' | 'locations' | 'confirm'>('plan');
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [meetingProposals, setMeetingProposals] = useState<MeetingProposal[]>([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [confirmedMeeting, setConfirmedMeeting] = useState<MeetingProposal | null>(null);

  // Form states
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    type: 'cafe' as const
  });

  const [newMeeting, setNewMeeting] = useState({
    date: '',
    time: '',
    locationId: '',
    duration: '60',
    notes: ''
  });

  useEffect(() => {
    // Listen to location suggestions
    const locationsQuery = query(
      collection(db, 'connectionFlows', connectionId, 'locationSuggestions'),
      orderBy('timestamp', 'desc')
    );
    
    const unsubscribeLocations = onSnapshot(locationsQuery, (snapshot) => {
      const locations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as LocationSuggestion));
      setLocationSuggestions(locations);
    });

    // Listen to meeting proposals
    const meetingsQuery = query(
      collection(db, 'connectionFlows', connectionId, 'meetingProposals'),
      orderBy('timestamp', 'desc')
    );
    
    const unsubscribeMeetings = onSnapshot(meetingsQuery, (snapshot) => {
      const meetings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as MeetingProposal));
      setMeetingProposals(meetings);
      
      // Check for confirmed meeting
      const confirmed = meetings.find(m => m.status === 'confirmed');
      if (confirmed) setConfirmedMeeting(confirmed);
    });

    return () => {
      unsubscribeLocations();
      unsubscribeMeetings();
    };
  }, [connectionId]);

  const suggestLocation = async () => {
    if (!newLocation.name || !newLocation.address) return;

    await addDoc(collection(db, 'connectionFlows', connectionId, 'locationSuggestions'), {
      ...newLocation,
      suggestedBy: currentUserId,
      status: 'proposed',
      timestamp: new Date()
    });

    setNewLocation({ name: '', address: '', type: 'cafe' });
    setShowLocationForm(false);
  };

  const acceptLocation = async (locationId: string) => {
    await updateDoc(
      doc(db, 'connectionFlows', connectionId, 'locationSuggestions', locationId),
      { status: 'accepted' }
    );
  };

  const proposeMeeting = async () => {
    const selectedLocation = locationSuggestions.find(l => l.id === newMeeting.locationId);
    if (!selectedLocation || !newMeeting.date || !newMeeting.time) return;

    await addDoc(collection(db, 'connectionFlows', connectionId, 'meetingProposals'), {
      ...newMeeting,
      location: selectedLocation,
      proposedBy: currentUserId,
      status: 'proposed',
      timestamp: new Date()
    });

    setNewMeeting({ date: '', time: '', locationId: '', duration: '60', notes: '' });
    setShowMeetingForm(false);
  };

  const confirmMeeting = async (meetingId: string) => {
    await updateDoc(
      doc(db, 'connectionFlows', connectionId, 'meetingProposals', meetingId),
      { status: 'confirmed' }
    );

    // Mark connection flow as completed
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      status: 'completed',
      completedAt: new Date(),
      finalStep: 'physical_meeting_confirmed'
    });
  };

  const getLocationTypeIcon = (type: string) => {
    const icons = {
      restaurant: '🍽️',
      cafe: '☕',
      office: '🏢',
      public: '🏛️',
      other: '📍'
    };
    return icons[type as keyof typeof icons] || '📍';
  };

  if (confirmedMeeting) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
            <span className="text-4xl">✅</span>
          </div>
          
          <h3 className="text-2xl font-semibold text-green-800 mb-4">
            Afspraak Bevestigd!
          </h3>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="space-y-2 text-left">
              <div><strong>Datum:</strong> {new Date(confirmedMeeting.date).toLocaleDateString('nl-NL')}</div>
              <div><strong>Tijd:</strong> {confirmedMeeting.time}</div>
              <div><strong>Locatie:</strong> {confirmedMeeting.location.name}</div>
              <div><strong>Adres:</strong> {confirmedMeeting.location.address}</div>
              <div><strong>Duur:</strong> {confirmedMeeting.duration} minuten</div>
              {confirmedMeeting.notes && (
                <div><strong>Notities:</strong> {confirmedMeeting.notes}</div>
              )}
            </div>
          </div>
          
          <p className="text-gray-600 mb-6">
            Jullie connectie flow is succesvol voltooid. Veel succes met jullie fysieke ontmoeting!
          </p>
          
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
              Toevoegen aan Agenda
            </button>
            <button className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              Deel Locatie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="bg-orange-50 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-orange-800 mb-2">Fysieke Ontmoeting</h3>
        <p className="text-orange-600 text-sm">
          Plan een fysieke ontmoeting. Stel locaties voor, plan een datum en tijd, en bevestig jullie afspraak.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          onClick={() => setActiveTab('plan')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'plan' 
              ? 'border-b-2 border-orange-500 text-orange-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Afspraak Plannen
        </button>
        <button
          onClick={() => setActiveTab('locations')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'locations' 
              ? 'border-b-2 border-orange-500 text-orange-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Locaties ({locationSuggestions.length})
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'plan' && (
          <div className="space-y-4">
            {/* Meeting Proposals */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Afspraak Voorstellen</h4>
                <button
                  onClick={() => setShowMeetingForm(true)}
                  disabled={locationSuggestions.filter(l => l.status === 'accepted').length === 0}
                  className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:bg-gray-300"
                >
                  + Nieuw Voorstel
                </button>
              </div>

              {locationSuggestions.filter(l => l.status === 'accepted').length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                  <p className="text-yellow-700 text-sm">
                    Eerst moet er een locatie geaccepteerd worden voordat je een afspraak kunt voorstellen.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {meetingProposals.map((proposal) => (
                  <div key={proposal.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium">
                          {new Date(proposal.date).toLocaleDateString('nl-NL')} om {proposal.time}
                        </div>
                        <div className="text-sm text-gray-600">
                          {getLocationTypeIcon(proposal.location.type)} {proposal.location.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {proposal.location.address}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        proposal.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        proposal.status === 'declined' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {proposal.status === 'proposed' ? 'In afwachting' : proposal.status}
                      </span>
                    </div>
                    
                    {proposal.notes && (
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Notities:</strong> {proposal.notes}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 mb-3">
                      Voorgesteld door: {proposal.proposedBy === currentUserId ? 'Jij' : 'Andere gebruiker'}
                    </div>
                    
                    {proposal.status === 'proposed' && proposal.proposedBy !== currentUserId && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => confirmMeeting(proposal.id)}
                          className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                        >
                          Bevestigen
                        </button>
                        <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                          Afwijzen
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Locatie Suggesties</h4>
              <button
                onClick={() => setShowLocationForm(true)}
                className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
              >
                + Locatie Voorstellen
              </button>
            </div>

            <div className="space-y-3">
              {locationSuggestions.map((location) => (
                <div key={location.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {getLocationTypeIcon(location.type)}
                        {location.name}
                      </div>
                      <div className="text-sm text-gray-600">{location.address}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      location.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      location.status === 'declined' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {location.status === 'proposed' ? 'Voorgesteld' : location.status}
                    </span>
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-3">
                    Voorgesteld door: {location.suggestedBy === currentUserId ? 'Jij' : 'Andere gebruiker'}
                  </div>
                  
                  {location.status === 'proposed' && location.suggestedBy !== currentUserId && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => acceptLocation(location.id)}
                        className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                      >
                        Accepteren
                      </button>
                      <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                        Afwijzen
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Location Form Modal */}
      {showLocationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="font-semibold mb-4">Locatie Voorstellen</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Naam</label>
                <input
                  value={newLocation.name}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Restaurant De Kroon"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Adres</label>
                <input
                  value={newLocation.address}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Hoofdstraat 123, Amsterdam"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={newLocation.type}
                  onChange={(e) => setNewLocation(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="cafe">Café</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="office">Kantoor</option>
                  <option value="public">Openbare ruimte</option>
                  <option value="other">Anders</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowLocationForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={suggestLocation}
                disabled={!newLocation.name || !newLocation.address}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300"
              >
                Voorstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Form Modal */}
      {showMeetingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h4 className="font-semibold mb-4">Afspraak Voorstellen</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Datum</label>
                <input
                  type="date"
                  value={newMeeting.date}
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Tijd</label>
                <input
                  type="time"
                  value={newMeeting.time}
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Locatie</label>
                <select
                  value={newMeeting.locationId}
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, locationId: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Kies een locatie</option>
                  {locationSuggestions.filter(l => l.status === 'accepted').map(location => (
                    <option key={location.id} value={location.id}>
                      {getLocationTypeIcon(location.type)} {location.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Duur (minuten)</label>
                <select
                  value={newMeeting.duration}
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, duration: e.target.value }))}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="30">30 minuten</option>
                  <option value="60">1 uur</option>
                  <option value="90">1,5 uur</option>
                  <option value="120">2 uur</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notities (optioneel)</label>
                <textarea
                  value={newMeeting.notes}
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Bijzondere wensen of opmerkingen..."
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowMeetingForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={proposeMeeting}
                disabled={!newMeeting.date || !newMeeting.time || !newMeeting.locationId}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300"
              >
                Voorstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


